import type {
  FindCaseRecordsResponse,
  GetCaseRecordResponse,
  UpdateAssignedRequest,
} from '@compass/client/types/case_record';
import { caseRecordService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface ReassignVars extends UpdateAssignedRequest {
  /** Workforce-cache scoping — the case's program id, used to target the right
   *  `useProgramCases` cache entries for optimistic update + prefix
   *  invalidation. (The fetch is keyed by program now, not team.) */
  programId: string;
  /** Set by the bulk-reassign flow so it can coalesce N parallel mutations into
   *  a single end-of-batch invalidation (see `useBulkReassign`). Per-card
   *  flow leaves it unset and the mutation invalidates on settle as before. */
  suppressInvalidate?: boolean;
}

/**
 * Reassign a case to another user. Uses the same `updateAssigned` endpoint as
 * the Case List page. When `team_id` is set (case being moved into one of the
 * user's eligible teams) the backend moves the case and validates the assignee against
 * the target team; when unset it validates against the case's current team.
 *
 * Optimistically patches the Workforce Console cache — every cached
 * `useProgramCases` entry under the case's (org, program) prefix, since the full
 * key also carries the assignee hash — rolls back on error; invalidates on
 * settle (unless the caller is batching).
 */
export const useReassignCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      programId: _programId,
      suppressInvalidate: _suppress,
      ...request
    }: ReassignVars) => caseRecordService.updateAssigned(request),

    onMutate: async vars => {
      const { organization_id, programId, case_record_id, assigned_user_id, team_id } = vars;
      if (!organization_id || !programId || !case_record_id) return { snapshots: [] };

      const prefix = queryKeys.workforce.casesByProgramPrefix(organization_id, programId);
      await queryClient.cancelQueries({ queryKey: prefix });

      const entries = queryClient.getQueriesData<FindCaseRecordsResponse>({ queryKey: prefix });
      const snapshots = entries.map(([queryKey, previous]) => ({ queryKey, previous }));

      for (const [queryKey, previous] of entries) {
        if (!previous?.case_records) continue;
        queryClient.setQueryData<FindCaseRecordsResponse>(queryKey, {
          ...previous,
          case_records: previous.case_records.map(
            (c): GetCaseRecordResponse =>
              c.id === case_record_id
                ? { ...c, assigned_user_id, ...(team_id ? { team_id } : {}) }
                : c,
          ),
        });
      }

      return { snapshots };
    },

    onError: (_err, _vars, context) => {
      if (context) {
        for (const snap of context.snapshots) {
          if (snap.previous) queryClient.setQueryData(snap.queryKey, snap.previous);
        }
      }
      // Generic copy — HIPAA-safe (no server message that may contain PHI).
      toast.error('Failed to reassign case. Please try again.');
    },

    onSettled: (_data, _err, vars) => {
      const { organization_id, programId, suppressInvalidate } = vars;
      if (suppressInvalidate) return;
      if (!organization_id || !programId) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.workforce.casesByProgramPrefix(organization_id, programId),
      });
    },
  });
};
